import React from 'react';
// PROVIDERS
import { ReactComponent as Facebook } from '../icons/facebook.svg';
import { ReactComponent as Instagram } from '../icons/instagram.svg';
import { ReactComponent as LinkedIn } from '../icons/linkedIn.svg';
import { ReactComponent as Pinterest } from '../icons/pinterest.svg';
import { ReactComponent as Twitter } from '../icons/x.svg';
import { ReactComponent as Tiktok } from '../icons/tiktok.svg';
import { ReactComponent as Bitly } from '../icons/bitly.svg';
import { ReactComponent as Google } from '../icons/google.svg';
import { ReactComponent as Threads } from '../icons/threads.svg';
// NETWORKS
import { ReactComponent as Youtube } from '../icons/youtube.svg';
// GENERIC SOCIAL NETWORKS
import { ReactComponent as Bluesky } from '../icons/blueskey.svg';
import { ReactComponent as Mastodon } from '../icons/mastodon.svg';
import { ReactComponent as Quora } from '../icons/quora.svg';
import { ReactComponent as Reddit } from '../icons/reddit.svg';
import { ReactComponent as Snapchat } from '../icons/snapchat.svg';
import { ReactComponent as Telegram } from '../icons/telegram.svg';
import { ReactComponent as WhatsApp } from '../icons/whatsapp.svg';
import { ReactComponent as Wordpress } from '../icons/wordpress.svg';

import classes from './SocialNetwork.module.scss';
import classNames from 'classnames';
import { capitalizeString } from '../../../helpers/capitalizeString';
import { GenericProfileType, Network, SocialOAuthProvider } from '../../../models/graphql';

export type SocialNetworkViewProps = {
  //
  type: 'square' | 'circle';
  provider: SocialOAuthProvider | null;
  network: Network;
  genericProfile?: GenericProfileType;
  size: 'small' | 'medium' | 'large';
  disabled?: boolean;
};

const getStyleNameByGenericProfileType = (type: GenericProfileType) => {
  switch (type) {
    case GenericProfileType.FACEBOOK_GROUP:
    case GenericProfileType.FACEBOOK_PERSONAL:
      return 'Facebook';
    case GenericProfileType.LINKEDIN_GROUP:
      return 'Linkedin';
    case GenericProfileType.INSTAGRAM_PERSONAL:
      return 'Instagram';
    default:
      return capitalizeString(type.toLowerCase());
  }
};

const getGenericSocialIconFromgenericProfile = (genericProfile: GenericProfileType) => {
  switch (genericProfile) {
    case GenericProfileType.FACEBOOK_PERSONAL:
    case GenericProfileType.FACEBOOK_GROUP:
      return <Facebook />;
    case GenericProfileType.LINKEDIN_GROUP:
      return <LinkedIn />;
    case GenericProfileType.INSTAGRAM_PERSONAL:
      return <Instagram />;
    case GenericProfileType.BLUESKY:
      return <Bluesky />;
    case GenericProfileType.MASTODON:
      return <Mastodon />;
    case GenericProfileType.QUORA:
      return <Quora />;
    case GenericProfileType.REDDIT:
      return <Reddit />;
    case GenericProfileType.SNAPCHAT:
      return <Snapchat />;
    case GenericProfileType.TELEGRAM:
      return <Telegram />;
    case GenericProfileType.WHATSAPP:
      return <WhatsApp />;
    case GenericProfileType.WORDPRESS:
      return <Wordpress />;
    // case GenericProfileType.YOUTUBE:
    //   return <Youtube />;
    default:
      return <></>;
  }
};

const getSocialIconByProvider = (
  provider: SocialOAuthProvider | null,
  network: Network,
  genericProfile?: GenericProfileType
) => {
  if (!provider && genericProfile) return getGenericSocialIconFromgenericProfile(genericProfile);

  switch (provider) {
    case SocialOAuthProvider.FACEBOOK:
      // need to handle IG through FB provider
      return network === Network.INSTAGRAM ? <Instagram /> : <Facebook />;
    case SocialOAuthProvider.INSTAGRAM:
      return <Instagram />;
    case SocialOAuthProvider.LINKEDIN:
      return <LinkedIn />;
    case SocialOAuthProvider.PINTEREST:
      return <Pinterest />;
    case SocialOAuthProvider.TWITTER:
      return <Twitter />;
    case SocialOAuthProvider.BITLY:
      return <Bitly />;
    case SocialOAuthProvider.GOOGLE:
      return network === Network.YOUTUBE ? <Youtube /> : <Google />;
    case SocialOAuthProvider.TIKTOK:
      return <Tiktok />;
    case SocialOAuthProvider.THREADS:
      return <Threads />;
    default:
      return !provider && genericProfile ? getStyleNameByGenericProfileType(genericProfile) : <></>;
  }
};

export const SocialNetworkView = React.memo((props: SocialNetworkViewProps): JSX.Element => {
  const Icon = getSocialIconByProvider(props.provider, props.network, props.genericProfile);
  const networkClass =
    !props.provider && props.genericProfile
      ? getStyleNameByGenericProfileType(props.genericProfile)
      : props.provider === SocialOAuthProvider.BITLY
      ? 'Bitly'
      : capitalizeString(props.network.toLowerCase());

  const componentClasses = classNames(
    classes[capitalizeString(props.type)],
    props.disabled ? [classes[`${networkClass}Disabled`]] : [classes[networkClass]],
    classes.SocialNetwork
  );
  const wrapperClasses = classNames(
    classes.SocialNetworkWrapper,
    [classes[capitalizeString(props.type)]],
    [classes[`SocialNetworkWrapper${capitalizeString(props.size)}`]]
  );
  return (
    <div className={wrapperClasses}>
      <div className={componentClasses}>{Icon}</div>
    </div>
  );
});

SocialNetworkView.displayName = 'SocialNetworkView';
